import React, { useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface MonthCalendarProps {
  value: Date;
  onChange: (date: Date) => void;
  /** Render the "SELECT DATE" overline + selected-date headline in the left rail. */
  showToolbar?: boolean;
}

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Always render a full 6-week grid so the component keeps the same height
// regardless of which month is shown (months span 4–6 weeks).
const TOTAL_CELLS = 42;

/**
 * Landscape month picker: a narrow left rail (selection headline + month
 * navigation) beside the day grid, so the whole control stays short.
 */
const MonthCalendar: React.FC<MonthCalendarProps> = ({ value, onChange, showToolbar = true }) => {
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(value));

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 });
    return Array.from({ length: TOTAL_CELLS }, (_, i) => addDays(start, i));
  }, [viewMonth]);

  return (
    <div className="flex h-full w-full flex-col">
      {showToolbar && (
        <p className="text-xs font-bold uppercase tracking-wider text-text-secondary">
          Select date
        </p>
      )}

      <div className="flex flex-1 items-center gap-5 pt-2 sm:gap-6">
        {/* Left rail: selection headline + month navigation, vertically
            centered beside the grid like the clock's readout rail */}
        <div className="flex w-32 shrink-0 flex-col gap-5 sm:w-36">
          <p className="text-lg font-bold text-text-primary">{format(value, 'EEE, MMM d')}</p>
          <div>
            <p className="text-sm font-semibold text-text-primary tabular-nums">
              {format(viewMonth, 'MMMM yyyy')}
            </p>
            <div className="-ml-1.5 mt-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                className="rounded-full p-1.5 text-text-secondary transition-colors hover:bg-primary-500/10 hover:text-text-primary focus-ring active:scale-[0.97]"
                aria-label="Previous month"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                className="rounded-full p-1.5 text-text-secondary transition-colors hover:bg-primary-500/10 hover:text-text-primary focus-ring active:scale-[0.97]"
                aria-label="Next month"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Day grid */}
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 grid grid-cols-7">
            {WEEKDAY_LABELS.map((label, i) => (
              <div
                key={`${label}-${i}`}
                className="py-0.5 text-center text-xs font-medium text-text-secondary"
              >
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {days.map((day) => {
              // Only the current month's days are interactive; adjacent-month days
              // collapse to fixed-height spacers so the grid stays aligned.
              if (!isSameMonth(day, viewMonth)) {
                return <div key={day.toISOString()} className="h-8" aria-hidden />;
              }

              const selected = isSameDay(day, value);
              const today = isToday(day);

              let circle =
                'flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors';
              if (selected) {
                circle += ' bg-primary-500 font-semibold text-white shadow-sm';
              } else if (today) {
                circle +=
                  ' text-primary-600 font-medium ring-1 ring-inset ring-primary-500 group-hover:bg-primary-500/10 dark:text-primary-300';
              } else {
                circle += ' text-text-primary group-hover:bg-primary-500/10';
              }

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => onChange(day)}
                  aria-pressed={selected}
                  aria-label={format(day, 'EEEE, MMMM d, yyyy')}
                  className="group flex h-8 items-center justify-center focus:outline-none active:scale-[0.97]"
                >
                  <span
                    className={`${circle} group-focus-visible:ring-2 group-focus-visible:ring-primary-500 group-focus-visible:ring-offset-2 dark:group-focus-visible:ring-offset-neutral-900`}
                  >
                    {format(day, 'd')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthCalendar;
